import { esc } from '../escape.js';
import { blurWrapMarkup } from '../planGate.js';
import { formatMoney } from '../currency.js';
import { computeTipPool } from '../tips.js';
import { businessToday, mondayOf } from '../scheduleTime.js';

// Fabricated staff cards for the free-plan locked preview - never real data,
// built with the same .payroll-group/.payroll-group-header classes the real
// (collapsed) staff cards use, so the blurred list looks exactly like the
// real one.
const FAKE_STAFF = [
  { initials: 'AM', name: 'Alex M.', color: '#22c55e', shifts: 5, hours: '32h 30m' },
  { initials: 'JR', name: 'Jamie R.', color: '#3b82f6', shifts: 4, hours: '24h 00m' },
  { initials: 'CL', name: 'Casey L.', color: '#f59e0b', shifts: 6, hours: '38h 15m' },
  { initials: 'MT', name: 'Morgan T.', color: '#ec4899', shifts: 3, hours: '18h 00m' },
];
const fakeStaffCardsMarkup = () => FAKE_STAFF.map((s) => `
  <div class="payroll-group">
    <div class="payroll-group-header payroll-group-header--collapsed">
      <div class="payroll-group-avatar" style="background:${s.color}">${s.initials}</div>
      <div class="payroll-group-name">${s.name}</div>
      <div class="payroll-group-count">${s.shifts} shifts</div>
      <div class="payroll-group-meta">${s.hours}</div>
      <svg class="payroll-group-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
    </div>
  </div>`).join('');

// Top-level Timesheets panel: a sidebar of sections (Timesheets / Corrections /
// Time Clock / Deleted shifts) + a per-staff filter, rendered from one data
// snapshot cached on the view. The controller refetches on period change; section
// and staff-filter switches re-render locally from the cache.

const AVATAR_COLORS = ['#22c55e','#3b82f6','#f59e0b','#ec4899','#8b5cf6','#06b6d4'];

// "Today" is the cafe's calendar day (business timezone), whatever zone this device is in.
const _todayDateValue  = () => businessToday();
const _todayMonthValue = () => businessToday().slice(0, 7);
const _todayQuarterValue = () => { const [y, m] = businessToday().split('-').map(Number); return `${y}-Q${Math.floor((m - 1) / 3) + 1}`; };
const _todayYear       = () => Number(businessToday().slice(0, 4));

const _currentWeekValue = () => mondayOf(businessToday());

const _prevNextValue = (type, value, delta) => {
  if (type === 'week') {
    const d = new Date(value + 'T00:00:00');
    d.setDate(d.getDate() + delta * 7);
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  }
  if (type === 'month') {
    const [y, m] = value.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
  }
  if (type === 'quarter') {
    const [y, q] = value.split('-Q').map(Number);
    const total = y * 4 + (q - 1) + delta;
    return `${Math.floor(total / 4)}-Q${(total % 4) + 1}`;
  }
  if (type === 'year') {
    return String(parseInt(value) + delta);
  }
  return value;
};

const _isFutureValue = (type, value) => {
  if (type === 'week')    return value >= _currentWeekValue();
  if (type === 'month')   return value >= _todayMonthValue();
  if (type === 'quarter') return value >= _todayQuarterValue();
  if (type === 'year')    return parseInt(value) >= _todayYear();
  return false;
};

const fmtTime = iso => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
const fmtDate = iso => new Date(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
// Compact numeric US date (mm/dd/yyyy) for exports, where spreadsheets parse it.
const fmtMDY  = iso => new Date(iso).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
const fmtHM   = ms  => {
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const workedMs = (shift) => {
  if (!shift.clockedOutAt) return null;
  const total = new Date(shift.clockedOutAt) - new Date(shift.clockedInAt);
  const breakMs = (shift.breaks ?? []).reduce((s, b) => {
    if (!b.endedAt) return s;
    return s + (new Date(b.endedAt) - new Date(b.startedAt));
  }, 0);
  return Math.max(0, total - breakMs);
};

const trashSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>`;

class TimesheetView {
  _panel   = document.querySelector('#timesheetPanel');
  _sidebar = document.querySelector('#timesheetSidebar');
  _content = document.querySelector('#timesheetContent');
  _lastTimeclockToken = null;   // freshly generated device code, kept across re-renders
  _genTokenHandler    = null;   // controller callback for "Generate Code"
  _locked     = false;          // free plan: sidebar stays real, but every tab shows the locked teaser
  _lockedData = null;           // last renderLockedTeaser() payload, for a sidebar re-click while locked
  _section     = 'timesheets';  // active sidebar section
  _staffFilter = null;          // staff id, or null = all staff
  _data        = null;          // last renderTimesheets args (for local re-renders)
  _expandedStaff = new Set();   // staffIds with their Timesheets card expanded
  _expandedMonths = new Set();  // `${staffId}::${yyyy-mm}` keys expanded (Quarter/Year only)
  _expandedWeeks = new Set();   // `${staffId}::${weekStartMs}` keys expanded within a card

  open() {
    this._panel.classList.remove('hidden', 'cashflow-exiting');
  }

  // Free-plan gate: real summary stat tiles (on-shift now / hours this week /
  // pending requests) stay live; the shift list, corrections inbox, device
  // registry, etc. below are locked. The real sidebar nav stays visible -
  // _renderSidebar reads this._locked and re-shows this same locked teaser
  // for any tab instead of switching for real.
  // data: { onShiftCount, weekShifts, pendingCount, canEdit, editRequests, leaveRequests }
  renderLockedTeaser(data) {
    this._lockedData = data; // kept so a re-click from _renderSidebar can re-render
    const { onShiftCount, weekShifts, pendingCount, canEdit = false, editRequests = [], leaveRequests = [] } = data;
    this._locked = true;
    this._data = { shifts: weekShifts ?? [], staff: [], canEdit, periodMeta: { type: 'week', value: '', label: 'This week' }, editRequests, deletedShifts: [], devices: [], leaveRequests, onShift: [] };
    this._renderSidebar();
    const weekMs = (weekShifts ?? []).reduce((sum, s) => sum + (workedMs(s) ?? 0), 0);
    const weekHours = (weekMs / 3600000).toFixed(1);
    // Reuses the real .ts-stat-strip/.ts-stat-tile component (see the
    // Timesheets-tab period-summary strip above) so this reads as production
    // UI, not a bespoke widget - just fed the three metrics that make sense
    // to surface for free (the strip itself normally shows different ones).
    const statTile = (accent, label, val, sub) => `
      <div class="ts-stat-tile" style="--tile-accent:${accent}">
        <div class="ts-stat-tile-label">${label}</div>
        <div class="ts-stat-tile-val">${val}</div>
        <div class="ts-stat-tile-sub">${sub}</div>
      </div>`;
    this._content.innerHTML = `
      <div class="ts-stat-strip">
        ${statTile('#18AE79', 'Hours This Week', `${weekHours}h`, 'across your team')}
        ${statTile('#3b82f6', 'On Shift Now', onShiftCount, 'staff clocked in')}
        ${statTile('#f59e0b', 'Pending Requests', pendingCount, 'corrections & leave')}
      </div>
      ${blurWrapMarkup('timesheets', fakeStaffCardsMarkup())}`;
  }

  // Preselect a sidebar section before data arrives (deep links, e.g. the home
  // sidebar's Time Clock shortcut).
  setSection(id) {
    this._section = id;
  }

  // Scrolls to + briefly flashes one request's row (notification bell item
  // click). Assumes the panel is already open on the matching section and
  // data has been rendered - call after renderTimesheets()/_render() resolve.
  focusCorrection(id) {
    this._section = 'corrections';
    this._render();
    const row = this._content.querySelector(`.ser-req[data-id="${esc(id)}"]`);
    if (!row) return;
    row.scrollIntoView({ block: 'center', behavior: 'smooth' });
    row.classList.add('ser-req--flash');
    setTimeout(() => row.classList.remove('ser-req--flash'), 1600);
  }

  focusLeaveRequest(id) {
    this._section = 'leave';
    this._render();
    // Pending requests render as .ts-lv-card, upcoming (approved) ones as
    // .ts-lv-row - a request id only ever matches one of the two.
    const row = this._content.querySelector(`.ts-lv-card[data-id="${esc(id)}"], .ts-lv-row[data-id="${esc(id)}"]`);
    if (!row) return;
    row.scrollIntoView({ block: 'center', behavior: 'smooth' });
    row.classList.add('ts-lv-card--flash');
    setTimeout(() => row.classList.remove('ts-lv-card--flash'), 1600);
  }

  close() {
    this._closeDrawer();
    this._panel.classList.add('cashflow-exiting');
    setTimeout(() => {
      this._panel.classList.add('hidden');
      this._panel.classList.remove('cashflow-exiting');
    }, 220);
  }

  // ── Mobile sidebar drawer ────────────────────────────────────────────────────
  // Below 800px the sidebar is an off-canvas drawer (shared .panel-drawer-btn /
  // .panel-nav-backdrop pattern from the Settings modal): opened from the
  // header hamburger, closed by the backdrop or picking a section/staff filter.
  // The open class lives on the <nav> itself, so sidebar re-renders (which only
  // replace innerHTML) don't slam it shut mid-look.

  _openDrawer() {
    this._sidebar?.classList.add('panel-sidebar--open');
    this._panel.querySelector('#timesheetNavBackdrop')?.classList.add('panel-nav-backdrop--visible');
    this._panel.querySelector('#timesheetNavBtn')?.setAttribute('aria-expanded', 'true');
  }

  _closeDrawer() {
    this._sidebar?.classList.remove('panel-sidebar--open');
    this._panel.querySelector('#timesheetNavBackdrop')?.classList.remove('panel-nav-backdrop--visible');
    this._panel.querySelector('#timesheetNavBtn')?.setAttribute('aria-expanded', 'false');
  }

  _addHandlerDrawer() {
    const btn = this._panel.querySelector('#timesheetNavBtn');
    btn?.addEventListener('click', () => {
      if (btn.getAttribute('aria-expanded') === 'true') this._closeDrawer();
      else this._openDrawer();
    });
    this._panel.querySelector('#timesheetNavBackdrop')?.addEventListener('click', () => this._closeDrawer());
  }

  _addHandlerOpen(handler) {
    document.addEventListener('click', (e) => {
      if (!e.target.closest("[data-action='timesheets']")) return;
      handler();
    });
  }

  _addHandlerClose(handler) {
    this._panel.querySelector('.timesheet-back')?.addEventListener('click', () => handler());
  }

  _periodChangeHandler = null;

  _addHandlerTimesheetPeriod(handler) {
    this._periodChangeHandler = handler;
  }

  // ── Tips data (Timesheets > Tips) ─────────────────────────────────────────
  // Set by the controller alongside renderTimesheets (manage_pay only).
  // { tips: [{ date, amount }], roles: [{ id, name }], poolRoleIds: null | [id], tipsEnabled }
  _tips = { tips: [], roles: [], poolRoleIds: null, tipsEnabled: false };
  setTipsData(data) {
    this._tips = { ...this._tips, ...data };
  }

  _tipPoolRolesHandler = null;
  _addHandlerTipPoolRoles(handler) {
    this._tipPoolRolesHandler = handler;
  }

  _addHandlerGenerateTimeclockToken(handler) {
    this._genTokenHandler = handler;
  }

  _reviewHandler = null;
  _addHandlerReviewRequest(handler) {
    this._reviewHandler = handler;
  }

  _deleteShiftsHandler = null;
  _addHandlerDeleteShifts(handler) {
    this._deleteShiftsHandler = handler;
  }

  _restoreShiftHandler = null;
  _addHandlerRestoreShift(handler) {
    this._restoreShiftHandler = handler;
  }

  // Cache the code so a re-render keeps showing it.
  showTimeclockToken(token) {
    this._lastTimeclockToken = token;
    const el = document.getElementById('payrollTcToken');
    if (el) { el.textContent = token; el.classList.remove('hidden'); }
    document.getElementById('payrollTcCopyBtn')?.classList.remove('hidden');
    const btn = document.getElementById('payrollGenTokenBtn');
    if (btn) btn.textContent = 'Regenerate Code';
  }

  _revokeDeviceHandler = null;
  _addHandlerRevokeDevice(handler) {
    this._revokeDeviceHandler = handler;
  }

  renderTimesheets(shifts, staff, canEdit = false, periodMeta = { type: 'week', value: '', label: '' }, editRequests = [], deletedShifts = [], devices = [], leaveRequests = [], onShift = []) {
    this._locked = false;
    this._data = { shifts, staff, canEdit, periodMeta, editRequests, deletedShifts, devices, leaveRequests, onShift };
    // Sections a viewer can't use fall back to Timesheets.
    if (!canEdit && (this._section === 'corrections' || this._section === 'deleted')) this._section = 'timesheets';
    // A fresh fetch (period nav) starts every staff/month/week card collapsed
    // again - a local re-render (staff filter, section switch) keeps whatever's open.
    this._expandedStaff = new Set();
    this._expandedMonths = new Set();
    this._expandedWeeks = new Set();
    this._render();
  }

  _render() {
    if (!this._data) return;
    this._renderSidebar();
    if (this._section === 'corrections')     this._renderCorrectionsSection();
    else if (this._section === 'timeclock')  this._renderTimeclockSection();
    else if (this._section === 'leave')      this._renderLeaveSection();
    else if (this._section === 'deleted')    this._renderDeletedSection();
    else if (this._section === 'tips' && this._data.canEdit) this._renderTipsSection();
    else                                     this._renderTimesheetsSection();
  }

  // ── Sidebar ─────────────────────────────────────────────────────────────────

  _renderSidebar() {
    const { canEdit, editRequests, deletedShifts, staff, leaveRequests } = this._data;
    const pendingLeave = leaveRequests.filter(r => r.status === 'pending').length;
    // Section nav items use the shared .pb-nav-item look (color tile + name +
    // one-line description). Icons are unique app-wide - check before reusing.
    const navSections = {
      timesheets:  { c: '#18AE79', desc: 'Hours &amp; pay periods', icon: '<rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="12" y1="9" x2="12" y2="21"/>' },
      corrections: { c: '#F59E0B', desc: 'Staff-requested fixes', icon: '<path d="M12 20h9"/><path d="M16.376 3.622a1 1 0 0 1 3.002 3.002L7.368 18.635a2 2 0 0 1-.855.506l-2.872.838a.5.5 0 0 1-.62-.62l.838-2.872a2 2 0 0 1 .506-.854z"/><path d="m15 5 3 3"/>' },
      leave:       { c: '#3B82F6', desc: 'Vacation &amp; sick days', icon: '<path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"/>' },
      timeclock:   { c: '#8B5CF6', desc: 'Kiosk devices', icon: '<circle cx="12" cy="13" r="8"/><path d="M12 9v4l2 2"/><path d="M5 3 2 6"/><path d="m22 6-3-3"/><path d="M6.38 18.7 4 21"/><path d="M17.64 18.67 20 21"/>' },
      tips:        { c: '#EC4899', desc: 'Tip pool by hours', icon: '<circle cx="8" cy="8" r="6"/><path d="M18.09 10.37A6 6 0 1 1 10.34 18"/><path d="M7 6h1v4"/><path d="m16.71 13.88.7.71-2.82 2.82"/>' },
      deleted:     { c: '#EF4444', desc: 'Restore within 7 days', icon: '<rect x="2" y="3" width="20" height="5" rx="1"/><path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8"/><path d="m9.5 17 5-5"/><path d="m9.5 12 5 5"/>' },
    };
    const tab = (id, label, count = 0) => `
      <button class="pb-nav-item ts-tab${this._section === id ? ' ts-tab--active' : ''}" data-section="${id}" type="button" style="--nav-c:${navSections[id].c}">
        <span class="pb-nav-icon"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${navSections[id].icon}</svg></span>
        <span class="pb-nav-text"><span class="pb-nav-name">${label}</span><span class="pb-nav-desc">${navSections[id].desc}</span></span>
        ${count ? `<span class="ts-tab-count">${count}</span>` : ''}
      </button>`;

    // Per-staff filter, shown while browsing timesheets. Lists active members;
    // members with shifts only in history still show up via their staff row.
    const activeStaff = (staff ?? []).filter(s => s.isActive !== false);
    const staffItems = this._section === 'timesheets' ? `
      <p class="ts-side-label">View staff</p>
      <button class="ts-staff-item${this._staffFilter === null ? ' ts-staff-item--active' : ''}" data-staff="" type="button">
        <span class="ts-staff-avatar ts-staff-avatar--all">
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
        </span>
        All staff
      </button>
      ${activeStaff.map((s, i) => {
        const name     = `${s.firstName ?? ''} ${s.lastName ?? ''}`.trim() || (s.email ?? '?');
        const initials = name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
        return `
          <button class="ts-staff-item${this._staffFilter === s.id ? ' ts-staff-item--active' : ''}" data-staff="${esc(s.id)}" type="button">
            <span class="ts-staff-avatar" style="background:${AVATAR_COLORS[i % AVATAR_COLORS.length]}">${esc(initials)}</span>
            ${esc(name)}
          </button>`;
      }).join('')}` : '';

    this._sidebar.innerHTML = `
      <p class="ts-side-label">Sections</p>
      ${tab('timesheets', 'Timesheets')}
      ${canEdit ? tab('corrections', 'Corrections', editRequests.length) : ''}
      ${tab('leave', 'Leave', canEdit ? pendingLeave : 0)}
      ${canEdit ? tab('tips', 'Tips') : ''}
      ${tab('timeclock', 'Time Clock')}
      ${canEdit ? tab('deleted', 'Deleted shifts', deletedShifts.length) : ''}
      ${staffItems}`;

    this._sidebar.querySelectorAll('.ts-tab').forEach(btn =>
      btn.addEventListener('click', () => {
        // Free plan: every section shows the same locked teaser - just move
        // the active-tab highlight, never actually switch to a real section.
        if (this._locked) {
          this._section = btn.dataset.section;
          this.renderLockedTeaser(this._lockedData);
          return;
        }
        if (btn.dataset.section === this._section) return this._closeDrawer();
        this._section = btn.dataset.section;
        this._render();
        this._closeDrawer();
      }));
    this._sidebar.querySelectorAll('.ts-staff-item').forEach(btn =>
      btn.addEventListener('click', () => {
        if (this._locked) return;
        this._staffFilter = btn.dataset.staff || null;
        this._render();
        this._closeDrawer();
      }));
  }

  // ── Shared period bar (Timesheets + Tips) ───────────────────────────────────
  // Week / Month / Quarter / Year / Custom tabs + prev/next nav. Both sections
  // load through the same _periodChangeHandler, so switching between them keeps
  // the chosen period.

  _periodBarHtml(periodMeta) {
    const { type, value, label } = periodMeta;
    const isCurrent  = type !== 'custom' && _isFutureValue(type, value);
    const chipLabel  = type === 'month' ? 'This month' : type === 'quarter' ? 'This quarter' : type === 'year' ? 'This year' : 'This week';
    const prevSvg    = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>`;
    const nextSvg    = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>`;

    const navHtml = type === 'custom'
      ? `<div class="payroll-period-custom">
          <input type="date" id="payrollCustomFrom" class="payroll-period-input" value="${value?.from ?? ''}" />
          <span class="payroll-period-sep">–</span>
          <input type="date" id="payrollCustomTo" class="payroll-period-input" value="${value?.to ?? ''}" />
          <button id="payrollCustomApply" class="btn primary" type="button" style="padding:4px 14px;font-size:0.82rem">Apply</button>
        </div>`
      : `<div class="payroll-period-nav">
          <button class="payroll-nav-btn" id="payrollPrev" type="button" aria-label="Previous">${prevSvg}</button>
          <span class="payroll-period-label">${label}</span>
          ${!isCurrent ? `<button class="payroll-period-chip" id="payrollToday" type="button">${chipLabel}</button>` : ''}
          <button class="payroll-nav-btn${isCurrent ? ' payroll-nav-btn--disabled' : ''}" id="payrollNext" type="button" aria-label="Next" ${isCurrent ? 'disabled' : ''}>${nextSvg}</button>
        </div>`;

    return `
      <div class="payroll-period-bar">
        <div class="payroll-period-tabs">
          ${['week','month','quarter','year','custom'].map(t =>
            `<button class="payroll-period-tab${type === t ? ' payroll-period-tab--active' : ''}" data-ptype="${t}" type="button">${t.charAt(0).toUpperCase() + t.slice(1)}</button>`
          ).join('')}
        </div>
        ${navHtml}
      </div>`;
  }

  _wirePeriodBar(periodMeta) {
    const { type, value } = periodMeta;
    const isCurrent = type !== 'custom' && _isFutureValue(type, value);
    this._content.querySelectorAll('.payroll-period-tab').forEach(btn => {
      btn.addEventListener('click', () => {
        const newType = btn.dataset.ptype;
        if (newType === type) return;
        let newValue;
        if (newType === 'week')         newValue = _currentWeekValue();
        else if (newType === 'month')   newValue = _todayMonthValue();
        else if (newType === 'quarter') newValue = _todayQuarterValue();
        else if (newType === 'year')    newValue = String(_todayYear());
        else                            newValue = { from: _todayDateValue(), to: _todayDateValue() };
        this._periodChangeHandler?.(newType, newValue);
      });
    });

    document.getElementById('payrollPrev')?.addEventListener('click', () => {
      this._periodChangeHandler?.(type, _prevNextValue(type, value, -1));
    });
    document.getElementById('payrollNext')?.addEventListener('click', () => {
      if (!isCurrent) this._periodChangeHandler?.(type, _prevNextValue(type, value, 1));
    });
    document.getElementById('payrollToday')?.addEventListener('click', () => {
      const curVal = type === 'month' ? _todayMonthValue() : type === 'quarter' ? _todayQuarterValue() : type === 'year' ? String(_todayYear()) : _currentWeekValue();
      this._periodChangeHandler?.(type, curVal);
    });
    document.getElementById('payrollCustomApply')?.addEventListener('click', () => {
      const from = document.getElementById('payrollCustomFrom')?.value;
      const to   = document.getElementById('payrollCustomTo')?.value;
      if (!from || !to) return;
      if (from > to) { alert('Start date must be before end date.'); return; }
      this._periodChangeHandler?.('custom', { from, to });
    });

  }

  // ── Section: Timesheets ─────────────────────────────────────────────────────

  _renderTimesheetsSection() {
    const { staff, canEdit, periodMeta } = this._data;
    const shifts = this._staffFilter
      ? this._data.shifts.filter(sh => sh.staffId === this._staffFilter)
      : this._data.shifts;
    // Deleting is deliberately limited to a WEEK's worth of shifts at once: the
    // Week view exposes it directly, and Month/Year group shifts by week with a
    // per-week delete - never a whole month or year in one click.
    const showDelete = canEdit && periodMeta.type === 'week';

    const byStaff = new Map();
    for (const sh of shifts) {
      if (!byStaff.has(sh.staffId)) byStaff.set(sh.staffId, []);
      byStaff.get(sh.staffId).push(sh);
    }

    const groups = [];
    let colorIdx = 0;
    for (const [staffId, shs] of byStaff) {
      const s        = staff.find(m => m.id === staffId);
      const name     = s ? `${s.firstName} ${s.lastName}`.trim() : shs[0]?.staffName ?? '?';
      const initials = name.split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase();
      const color    = AVATAR_COLORS[colorIdx++ % AVATAR_COLORS.length];
      const totalMs  = shs.reduce((acc, sh) => { const ms = workedMs(sh); return ms != null ? acc + ms : acc; }, 0);
      // Hourly rate / gross pay is intentionally NOT surfaced (hidden from the UI
      // for now; the staff.hourly_rate column stays in the schema untouched).
      groups.push({ staffId, shs, name, initials, color, totalMs });
    }

    // Period summary stat strip - answers "how's this period looking" before
    // scrolling into any staff card. Skipped when there's nothing to summarize.
    let statStripHtml = '';
    if (shifts.length > 0) {
      const totalMs = shifts.reduce((acc, sh) => { const ms = workedMs(sh); return ms != null ? acc + ms : acc; }, 0);
      const completed = shifts.filter(sh => workedMs(sh) != null).length;
      const avgMs = completed ? Math.round(totalMs / completed) : 0;
      const uniqueDays = new Set(shifts.map(sh => new Date(sh.clockedInAt).toDateString())).size;
      const statTile = (accent, label, val, sub) => `
        <div class="ts-stat-tile" style="--tile-accent:${accent}">
          <div class="ts-stat-tile-label">${label}</div>
          <div class="ts-stat-tile-val">${val}</div>
          <div class="ts-stat-tile-sub">${sub}</div>
        </div>`;
      statStripHtml = `
        <div class="ts-stat-strip">
          ${statTile('#18AE79', 'Total Hours', fmtHM(totalMs), 'this period')}
          ${statTile('#3b82f6', 'Shifts Worked', shifts.length, `across ${byStaff.size} staff`)}
          ${statTile('#8b5cf6', 'Days Worked', uniqueDays, 'this period')}
          ${statTile('#f59e0b', 'Avg. Shift Length', completed ? fmtHM(avgMs) : '–', 'per completed shift')}
        </div>`;
    }

    // Staff cards are collapsed to one summary row by default - raw shift rows
    // only render for a card (and, within it, a week) the viewer actually opened.
    // This is what keeps the panel legible once a year of shifts has piled up.
    const renderEntry = (sh) => {
      const ms        = workedMs(sh);
      const isOpen     = !sh.clockedOutAt;
      const openBadge = isOpen ? `<span class="payroll-badge-active">Active</span>` : '';

      const allBreaks = sh.breaks ?? [];
      const breakSubRows = allBreaks.map(b => {
        const bMs  = b.endedAt ? new Date(b.endedAt) - new Date(b.startedAt) : null;
        return `
          <div class="payroll-break-row">
            <div class="payroll-break-label">Break</div>
            <div>${fmtTime(b.startedAt)} → ${b.endedAt ? fmtTime(b.endedAt) : '–'}</div>
            <div class="payroll-break-dur">${bMs !== null ? fmtHM(bMs) : '–'}</div>
            <div></div>
          </div>`;
      }).join('');

      // Provenance: normal clock-tracked shifts get no note; anything touched
      // by a human is flagged (approved request > admin edit > manual add).
      let provNote = '';
      if (sh.approvedBy) {
        provNote = `<div class="payroll-prov-note payroll-prov-note--approved">Corrected via request · approved by ${esc(sh.approvedBy)}</div>`;
      } else if (sh.source === 'request') {
        provNote = `<div class="payroll-prov-note payroll-prov-note--approved">Corrected via request</div>`;
      } else if (sh.editedBy) {
        provNote = `<div class="payroll-prov-note payroll-prov-note--edited">${sh.source === 'manual' ? 'Added manually' : 'Edited'} by ${esc(sh.editedBy)}</div>`;
      } else if (sh.source === 'manual') {
        provNote = `<div class="payroll-prov-note payroll-prov-note--edited">Added manually</div>`;
      }
      return `
        <div class="payroll-shift-entry">
          <div class="payroll-shift-row">
            <div class="payroll-shift-date">${fmtDate(sh.clockedInAt)}</div>
            <div class="payroll-shift-times">
              ${fmtTime(sh.clockedInAt)} → ${sh.clockedOutAt ? fmtTime(sh.clockedOutAt) : '–'}
              ${openBadge}
            </div>
            <div class="payroll-shift-hrs">${ms !== null ? fmtHM(ms) : '–'}</div>
            <div class="payroll-shift-actions">
              ${canEdit ? `<button class="payroll-edit-btn" data-id="${sh.id}" type="button">Edit</button>` : ''}
              ${showDelete ? `<button class="payroll-del-btn" data-id="${sh.id}" data-desc="${esc(`${fmtDate(sh.clockedInAt)}'s shift`)}" type="button" aria-label="Delete shift">${trashSvg}</button>` : ''}
            </div>
          </div>
          ${provNote}
          ${breakSubRows}
        </div>`;
    };

    // Week view: flat rows, no further collapse level (there's only one week).
    // Month view: group by week, each week collapsed by default behind its own
    // subtotal row. Quarter/Year: an extra Month level sits between staff and
    // week (Staff -> Month -> Week -> shifts) - a whole quarter/year of
    // uncollapsed weeks is still a lot to page through. The delete unit stays a
    // WEEK regardless of how many collapse levels sit above it, never a whole
    // month/quarter/year in one click.
    const groupsByMonth = periodMeta.type === 'quarter' || periodMeta.type === 'year';
    const groupsByWeek  = groupsByMonth || periodMeta.type === 'month';
    const weekStart = (iso) => {
      const d = new Date(iso);
      d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); // Monday
      d.setHours(0, 0, 0, 0);
      return d;
    };

    let groupsHtml = '';
    if (groups.length === 0) {
      const who = this._staffFilter ? 'this staff member' : 'this period';
      groupsHtml = `<p class="staff-empty" style="padding:24px 0">No shifts recorded for ${who}.</p>`;
    } else {
      groupsHtml = groups.map(g => {
        const staffExpanded = this._expandedStaff.has(g.staffId);
        const weekDelBtn = showDelete && g.shs.length ? `
          <button class="payroll-week-del" type="button"
            data-ids="${esc(g.shs.map(s => s.id).join(','))}"
            data-desc="${esc(`${g.name}'s ${g.shs.length} shift${g.shs.length === 1 ? '' : 's'} this week`)}">
            ${trashSvg} Delete week
          </button>` : '';

        // Groups a slice of this staff member's shifts into collapsible week
        // rows - shared by the Month path (whole g.shs) and the Quarter/Year
        // path (one calendar month's worth of shifts at a time).
        const renderWeeks = (shs) => {
          const byWeek = new Map();
          for (const sh of shs) {
            const key = weekStart(sh.clockedInAt).getTime();
            if (!byWeek.has(key)) byWeek.set(key, []);
            byWeek.get(key).push(sh);
          }
          return [...byWeek.entries()].map(([key, weekShs]) => {
            const start     = new Date(key);
            const end       = new Date(key); end.setDate(end.getDate() + 6);
            const wLabel    = `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
            const weekKey   = `${g.staffId}::${key}`;
            const weekOpen  = this._expandedWeeks.has(weekKey);
            const weekMs    = weekShs.reduce((acc, sh) => { const ms = workedMs(sh); return ms != null ? acc + ms : acc; }, 0);
            const delBtn = canEdit ? `
              <button class="payroll-week-del" type="button"
                data-ids="${esc(weekShs.map(s => s.id).join(','))}"
                data-desc="${esc(`${g.name}'s ${weekShs.length} shift${weekShs.length === 1 ? '' : 's'} (week of ${wLabel})`)}">
                ${trashSvg}
              </button>` : '';
            return `
              <div class="payroll-week-sep" data-week-toggle data-week-key="${esc(weekKey)}">
                <span>Week of ${wLabel}</span>
                <span class="payroll-week-sep-meta">
                  <span class="payroll-week-sep-count">${weekShs.length} shift${weekShs.length === 1 ? '' : 's'}</span>
                  <span class="payroll-week-sep-hrs">${fmtHM(weekMs)}</span>
                  ${delBtn}
                  <svg class="payroll-week-sep-chevron${weekOpen ? ' payroll-week-sep-chevron--open' : ''}" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                </span>
              </div>
              ${weekOpen ? weekShs.map(renderEntry).join('') : ''}`;
          }).join('');
        };

        let bodyHtml = '';
        if (staffExpanded) {
          if (groupsByMonth) {
            const byMonth = new Map();
            for (const sh of g.shs) {
              const d    = new Date(sh.clockedInAt);
              const mKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
              if (!byMonth.has(mKey)) byMonth.set(mKey, []);
              byMonth.get(mKey).push(sh);
            }
            bodyHtml = [...byMonth.entries()].map(([mKey, monthShs]) => {
              const [my, mm]  = mKey.split('-').map(Number);
              const mLabel    = new Date(my, mm - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
              const monthKey  = `${g.staffId}::${mKey}`;
              const monthOpen = this._expandedMonths.has(monthKey);
              const monthMs   = monthShs.reduce((acc, sh) => { const ms = workedMs(sh); return ms != null ? acc + ms : acc; }, 0);
              return `
                <div class="payroll-week-sep payroll-month-sep" data-month-toggle data-month-key="${esc(monthKey)}">
                  <span>${mLabel}</span>
                  <span class="payroll-week-sep-meta">
                    <span class="payroll-week-sep-count">${monthShs.length} shift${monthShs.length === 1 ? '' : 's'}</span>
                    <span class="payroll-week-sep-hrs">${fmtHM(monthMs)}</span>
                    <svg class="payroll-week-sep-chevron${monthOpen ? ' payroll-week-sep-chevron--open' : ''}" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                  </span>
                </div>
                ${monthOpen ? renderWeeks(monthShs) : ''}`;
            }).join('');
          } else if (groupsByWeek) {
            bodyHtml = renderWeeks(g.shs);
          } else {
            bodyHtml = g.shs.map(renderEntry).join('');
          }
        }

        return `
          <div class="payroll-group">
            <div class="payroll-group-header${staffExpanded ? '' : ' payroll-group-header--collapsed'}" data-staff-toggle data-staff-id="${esc(g.staffId)}">
              <div class="payroll-group-avatar" style="background:${g.color}">${esc(g.initials)}</div>
              <div class="payroll-group-name">${esc(g.name)}</div>
              <div class="payroll-group-count">${g.shs.length} shift${g.shs.length === 1 ? '' : 's'}</div>
              <div class="payroll-group-meta">${fmtHM(g.totalMs)}</div>
              ${weekDelBtn}
              <svg class="payroll-group-chevron${staffExpanded ? ' payroll-group-chevron--open' : ''}" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
            </div>
            ${staffExpanded ? `<div class="payroll-group-shifts">${bodyHtml}</div>` : ''}
          </div>`;
      }).join('');
    }

    const { type, value, label } = periodMeta;

    const exportCsv = () => {
      const rows = [];
      for (const g of groups) {
        for (const sh of g.shs) {
          const ms     = workedMs(sh);
          const bMs    = (sh.breaks ?? []).reduce((s, b) => {
            if (!b.endedAt) return s;
            return s + (new Date(b.endedAt) - new Date(b.startedAt));
          }, 0);
          rows.push([
            g.name,
            fmtMDY(sh.clockedInAt),
            fmtTime(sh.clockedInAt),
            sh.clockedOutAt ? fmtTime(sh.clockedOutAt) : '',
            bMs > 0 ? fmtHM(bMs) : '',
            ms !== null ? fmtHM(ms) : '',
          ]);
        }
      }
      rows.unshift(['Staff', 'Date', 'Clock In', 'Clock Out', 'Break', 'Hours Worked']);
      rows.unshift([`Pointbunny Timesheets – ${label}`]);
      // A cell starting with = + - @ (or tab/CR) is a live formula to Excel/Sheets even
      // inside quotes - prefix with ' so a crafted staff name can't execute on reopen.
      const csvCell = (v) => {
        let s = String(v);
        if (/^[=+\-@\t\r]/.test(s) && !Number.isFinite(Number(s))) s = `'${s}`;
        return `"${s.replace(/"/g, '""')}"`;
      };
      const csv  = rows.map(r => r.map(csvCell).join(',')).join('\n');
      const slug = label.replace(/\s+[–-]\s+|[^a-z0-9]+/gi, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = `timesheets-${slug}.csv`; a.click();
      URL.revokeObjectURL(url);
    };

    const exportPdf = () => {
      const sumRows = groups.map(g => `
        <tr>
          <td>${esc(g.name)}</td>
          <td class="hrs">${fmtHM(g.totalMs)}</td>
        </tr>`).join('');

      const details = groups.map(g => {
        const shiftRows  = g.shs.map(sh => {
          const ms    = workedMs(sh);
          const bMs   = (sh.breaks ?? []).reduce((s, b) => {
            if (!b.endedAt) return s;
            return s + (new Date(b.endedAt) - new Date(b.startedAt));
          }, 0);
          return `<tr>
            <td>${fmtDate(sh.clockedInAt)}</td>
            <td>${fmtTime(sh.clockedInAt)}</td>
            <td>${sh.clockedOutAt ? fmtTime(sh.clockedOutAt) : '–'}</td>
            <td>${bMs > 0 ? fmtHM(bMs) : '–'}</td>
            <td class="hrs">${ms !== null ? fmtHM(ms) : '–'}</td>
          </tr>`;
        }).join('');
        return `
          <h2>${esc(g.name)}</h2>
          <table>
            <thead><tr><th>Date</th><th>Clock In</th><th>Clock Out</th><th>Break</th><th>Hours</th></tr></thead>
            <tbody>${shiftRows}</tbody>
          </table>`;
      }).join('');

      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
        <title>Timesheets – ${label}</title>
        <style>
          *{box-sizing:border-box}body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;padding:32px;color:#111;font-size:14px}
          h1{font-size:1.3rem;margin:0 0 2px}
          .period{color:#555;font-size:.85rem;margin-bottom:20px}
          h2{font-size:.95rem;margin:20px 0 6px;border-bottom:2px solid #e5e7eb;padding-bottom:4px}
          table{width:100%;border-collapse:collapse;margin-bottom:8px}
          th{text-align:left;font-size:.78rem;text-transform:uppercase;letter-spacing:.03em;color:#666;border-bottom:1.5px solid #e5e7eb;padding:5px 8px}
          td{padding:7px 8px;font-size:.85rem;border-bottom:1px solid #f0f0f0}
          .hrs{color:#16a34a;font-weight:700}
          @media print{body{padding:12px}}
        </style></head><body>
        <h1>Timesheets</h1>
        <p class="period">${label}</p>
        ${groups.length > 0 ? `
          <h2>Summary</h2>
          <table><thead><tr><th>Staff</th><th>Total Hours</th></tr></thead>
          <tbody>${sumRows}</tbody></table>
          ${details}` : '<p>No shifts recorded for this period.</p>'}
      </body></html>`;

      const win = window.open('', '_blank', 'width=820,height=900');
      if (!win) { alert('Allow pop-ups to export PDF.'); return; }
      win.document.write(html);
      win.document.close();
      win.focus();
      setTimeout(() => win.print(), 400);
    };

    const dlIcon    = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>`;
    const chevDown  = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>`;

    const filteredName = this._staffFilter
      ? (staff.find(s => s.id === this._staffFilter)
          ? `${staff.find(s => s.id === this._staffFilter).firstName ?? ''} ${staff.find(s => s.id === this._staffFilter).lastName ?? ''}`.trim()
          : 'Staff')
      : null;

    this._content.innerHTML = `
      <div class="payroll-header">
        <h3 class="payroll-title">${filteredName ? `${esc(filteredName)}'s Timesheet` : 'Timesheets'}</h3>
        <div class="payroll-header-actions">
          <div class="payroll-export-menu" id="payrollExportMenu">
            <button class="payroll-export-btn" id="payrollExportToggle" type="button">
              ${dlIcon} Export ${chevDown}
            </button>
            <div class="payroll-export-dropdown hidden" id="payrollExportDropdown">
              <button class="payroll-export-option" id="payrollExportPdf" type="button">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                PDF
              </button>
              <button class="payroll-export-option" id="payrollExportCsv" type="button">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M3 15h18M9 3v18"/></svg>
                CSV / Excel
              </button>
            </div>
          </div>
          ${canEdit ? `<button class="btn primary" id="payrollAddShiftBtn" type="button">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M8 12h8"/><path d="M12 8v8"/></svg>
            Add Shift
          </button>` : ''}
        </div>
      </div>
      ${this._periodBarHtml(periodMeta)}
      ${statStripHtml}
      ${groupsHtml}
    `;

    this._wirePeriodBar(periodMeta);
    this._content.querySelectorAll('.payroll-del-btn').forEach(btn =>
      btn.addEventListener('click', () => this._deleteShiftsHandler?.([btn.dataset.id], btn.dataset.desc)));
    // stopPropagation: this button now sits inside a staff/week header row that
    // toggles expand/collapse on click - deleting must not also toggle it.
    this._content.querySelectorAll('.payroll-week-del').forEach(btn =>
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._deleteShiftsHandler?.(btn.dataset.ids.split(','), btn.dataset.desc);
      }));

    this._content.querySelectorAll('[data-staff-toggle]').forEach(el =>
      el.addEventListener('click', (e) => {
        if (e.target.closest('.payroll-week-del')) return;
        const id = el.dataset.staffId;
        if (this._expandedStaff.has(id)) this._expandedStaff.delete(id);
        else this._expandedStaff.add(id);
        this._renderTimesheetsSection();
      }));
    this._content.querySelectorAll('[data-month-toggle]').forEach(el =>
      el.addEventListener('click', () => {
        const key = el.dataset.monthKey;
        if (this._expandedMonths.has(key)) this._expandedMonths.delete(key);
        else this._expandedMonths.add(key);
        this._renderTimesheetsSection();
      }));
    this._content.querySelectorAll('[data-week-toggle]').forEach(el =>
      el.addEventListener('click', (e) => {
        if (e.target.closest('.payroll-week-del')) return;
        const key = el.dataset.weekKey;
        if (this._expandedWeeks.has(key)) this._expandedWeeks.delete(key);
        else this._expandedWeeks.add(key);
        this._renderTimesheetsSection();
      }));

    const exportToggle   = document.getElementById('payrollExportToggle');
    const exportDropdown = document.getElementById('payrollExportDropdown');
    const closeExportMenu = () => {
      exportDropdown?.classList.add('hidden');
      exportToggle?.classList.remove('payroll-export-btn--open');
    };
    exportToggle?.addEventListener('click', (e) => {
      e.stopPropagation();
      const opening = exportDropdown.classList.contains('hidden');
      exportDropdown.classList.toggle('hidden');
      exportToggle.classList.toggle('payroll-export-btn--open', opening);
      if (opening) setTimeout(() => document.addEventListener('click', closeExportMenu, { once: true }), 0);
    });
    document.getElementById('payrollExportPdf')?.addEventListener('click', (e) => {
      e.stopPropagation(); closeExportMenu(); exportPdf();
    });
    document.getElementById('payrollExportCsv')?.addEventListener('click', (e) => {
      e.stopPropagation(); closeExportMenu(); exportCsv();
    });

    if (canEdit) {
      document.getElementById('payrollAddShiftBtn')?.addEventListener('click', () => this._showShiftModal(null, staff));
      this._content.querySelectorAll('.payroll-edit-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          const sh = shifts.find(s => s.id === btn.dataset.id);
          if (sh) this._showShiftModal(sh, staff);
        });
      });
    }
  }

  // ── Section: Corrections (review inbox) ─────────────────────────────────────

  _renderCorrectionsSection() {
    const { editRequests } = this._data;

    const fmtTimeMaybe = iso => (iso ? fmtTime(iso) : '—');
    const breakSummary = (breaks) => {
      if (!Array.isArray(breaks) || !breaks.length) return '';
      return `<div class="ser-req-breaks">Breaks: ${breaks.map(b =>
        `${fmtTimeMaybe(b.start)}–${fmtTimeMaybe(b.end)}`).join(', ')}</div>`;
    };
    const reqRow = (r) => {
      const isMissed = !r.shiftId;
      const dateIso  = r.proposedIn ?? r.currentIn;
      const kind     = isMissed ? 'Missed shift' : 'Correction';
      const change   = isMissed
        ? `<span class="ser-req-new">${fmtTimeMaybe(r.proposedIn)} – ${fmtTimeMaybe(r.proposedOut)}</span>`
        : `<span class="ser-req-old">${fmtTimeMaybe(r.currentIn)} – ${fmtTimeMaybe(r.currentOut)}</span>
           <span class="ser-req-arrow">→</span>
           <span class="ser-req-new">${fmtTimeMaybe(r.proposedIn)} – ${fmtTimeMaybe(r.proposedOut)}</span>`;
      return `
        <li class="ser-req" data-id="${esc(r.id)}">
          <div class="ser-req-main">
            <div class="ser-req-head">
              <span class="ser-req-name">${esc(r.staffName || 'Staff')}</span>
              <span class="ser-req-kind ser-req-kind--${isMissed ? 'missed' : 'edit'}">${kind}</span>
              <span class="ser-req-date">${dateIso ? fmtDate(dateIso) : ''}</span>
            </div>
            <div class="ser-req-change">${change}</div>
            ${breakSummary(r.proposedBreaks)}
            ${r.reason ? `<div class="ser-req-reason">“${esc(r.reason)}”</div>` : ''}
          </div>
          <div class="ser-req-actions">
            <button class="btn ser-reject" data-id="${esc(r.id)}" type="button">Reject</button>
            <button class="btn primary ser-approve" data-id="${esc(r.id)}" type="button">Approve</button>
          </div>
        </li>`;
    };

    this._content.innerHTML = `
      <div class="payroll-header">
        <h3 class="payroll-title">Corrections</h3>
      </div>
      <p class="ts-section-sub">Shift corrections and missed-shift reports filed by staff from the Time Clock. Approving applies the proposed times immediately.</p>
      ${editRequests.length ? `
        <div class="ser-inbox ser-inbox--page">
          <ul class="ser-req-list">${editRequests.map(reqRow).join('')}</ul>
        </div>` : `
        <div class="ts-empty">
          <svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          <p>All caught up - no corrections waiting for review.</p>
        </div>`}
    `;

    this._content.querySelectorAll('.ser-approve').forEach(btn =>
      btn.addEventListener('click', () => this._reviewHandler?.(btn.dataset.id, 'approved')));
    this._content.querySelectorAll('.ser-reject').forEach(btn =>
      btn.addEventListener('click', () => this._reviewHandler?.(btn.dataset.id, 'rejected')));
  }

  // ── Section: Time Clock (device setup + registry) ───────────────────────────

  _renderTimeclockSection() {
    const { canEdit, devices, onShift = [] } = this._data;

    const fmtSince = iso => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    const onlineRows = onShift.length ? onShift.map(s => `
      <li class="tc-online-row">
        <span class="onshift-dot${s.onBreak ? ' onshift-dot--break' : ''}"></span>
        <span class="tc-online-name">${esc(s.name || 'Staff')}</span>
        <span class="tc-online-meta">${s.onBreak ? 'On break' : `since ${fmtSince(s.clockedInAt)}`}</span>
      </li>`).join('') : '';

    this._content.innerHTML = `
      <div class="payroll-header">
        <h3 class="payroll-title">Time Clock</h3>
      </div>
      <button class="tc-open-cta" id="payrollOpenTimeclock" type="button">
        <span class="tc-open-cta-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
        </span>
        <span class="tc-open-cta-text">
          <span class="tc-open-cta-title">Open Time Clock</span>
          <span class="tc-open-cta-sub">The page staff clock in and out on. Opens in a new tab, works on any tablet or shared device.</span>
        </span>
        <span class="tc-open-cta-arrow">
          <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
        </span>
      </button>
      <div class="tc-online-card">
        <div class="tc-online-header">
          <span class="tc-online-title">Currently timed in</span>
          <span class="tc-online-count${onShift.length ? '' : ' tc-online-count--zero'}">${onShift.length}</span>
        </div>
        ${onShift.length
          ? `<ul class="tc-online-list">${onlineRows}</ul>`
          : `<p class="tc-online-empty">No one is timed in right now.</p>`}
      </div>
      ${canEdit ? `
      <div class="payroll-tc-card payroll-tc-card--device">
        <div class="payroll-tc-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
        </div>
        <div class="payroll-tc-body">
          <div class="payroll-tc-title">Set up a device</div>
          <div class="payroll-tc-desc">Register a tablet or shared device once with a code, then staff clock in and out on it. Generating a new code invalidates the old one. We recommend just one device per location - it's meant to be shared, not personal.</div>
          <div class="payroll-tc-device-row">
            <span class="tc-token-badge${this._lastTimeclockToken ? '' : ' hidden'}" id="payrollTcToken">${esc(this._lastTimeclockToken ?? '')}</span>
            <button class="btn payroll-copy-btn${this._lastTimeclockToken ? '' : ' hidden'}" id="payrollTcCopyBtn" type="button">Copy</button>
            <button class="btn" id="payrollGenTokenBtn" type="button">${this._lastTimeclockToken ? 'Regenerate Code' : 'Generate Code'}</button>
          </div>
          <!-- .payroll-tc-multi-warn removed 2026-09-19: Pro is now unlimited,
               so the "can hit your plan's sign-in limit" claim it made is no
               longer true. Bring it back once the Additional Terminal add-on
               ships - CSS still defined in pointbunny.css. -->
          ${devices.length ? `
          <ul class="payroll-devices">
            ${devices.map(d => `
            <li class="payroll-device-row">
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="2" width="16" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>
              <span class="payroll-device-name">${esc(d.label)}</span>
              <span class="payroll-device-meta">Activated ${new Date(d.activatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
              <button class="payroll-device-del" data-id="${esc(d.id)}" data-label="${esc(d.label)}" type="button" aria-label="Remove device">&times;</button>
            </li>`).join('')}
          </ul>` : ''}
        </div>
      </div>` : ''}
      <details class="payroll-info-card">
        <summary class="payroll-info-summary">
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
          How does the Time Clock work?
          <svg class="payroll-info-chevron" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
        </summary>
        <ol class="payroll-info-steps">
          <li><strong>Set up a device:</strong> Click <em>Generate Code</em> above for a 6-character activation code.</li>
          <li><strong>Activate it:</strong> Open <em>Time Clock</em> on the tablet you'll use, tap Activate, and enter that code. The device only needs this once.</li>
          <li><strong>Staff sign in:</strong> Each staff member logs in with their own account (not the POS owner account), then clocks in, takes breaks, and clocks out.</li>
          <li><strong>Shifts appear here:</strong> Every clock-in/out is recorded as a shift in the Timesheets section. Admins can also manually add or edit shifts with <em>Add Shift</em>.</li>
        </ol>
      </details>
    `;

    document.getElementById('payrollOpenTimeclock')?.addEventListener('click', () => {
      // Clean /timeclock URL via the Netlify rewrite; the dev server has no
      // rewrites, so localhost opens the .html directly.
      window.open(location.hostname === 'localhost' ? 'timeclock.html' : '/timeclock', '_blank');
    });

    document.getElementById('payrollGenTokenBtn')?.addEventListener('click', () => this._genTokenHandler?.());

    const copyBtn = document.getElementById('payrollTcCopyBtn');
    copyBtn?.addEventListener('click', async () => {
      if (!this._lastTimeclockToken) return;
      try {
        await navigator.clipboard.writeText(this._lastTimeclockToken);
        copyBtn.textContent = 'Copied!';
        setTimeout(() => { copyBtn.textContent = 'Copy'; }, 1600);
      } catch (_) { /* clipboard unavailable (http/permissions) - leave as-is */ }
    });

    this._content.querySelectorAll('.payroll-device-del').forEach(btn =>
      btn.addEventListener('click', () => this._revokeDeviceHandler?.(btn.dataset.id, btn.dataset.label)));
  }

  // ── Section: Leave (vacation / sick requests) ──────────────────────────────

  // ── Section: Tips ───────────────────────────────────────────────────────────
  // Each day's tips split among the people in the pool who worked that day, by
  // hours (tips.js computeTipPool). The owner is never in the pool; which roles
  // are is chosen here (null = every role except Admin).

  _tipPoolRoleIds() {
    const { roles, poolRoleIds } = this._tips;
    return poolRoleIds ?? roles.filter(r => r.name !== 'Admin').map(r => r.id);
  }

  _renderTipsSection() {
    const { staff, periodMeta } = this._data;
    const { tips, roles, tipsEnabled } = this._tips;
    const poolIds = new Set(this._tipPoolRoleIds());
    const pool = computeTipPool({
      tips,
      shifts: this._data.shifts,
      inPool: (sh) => !sh.staffIsOwner && (sh.staffRoleId == null
        ? this._tips.poolRoleIds == null   // no role on record: default pool only
        : poolIds.has(sh.staffRoleId)),
    });

    const nameOf = (staffId) => {
      const s = staff.find(m => m.id === staffId);
      if (s) return `${s.firstName ?? ''} ${s.lastName ?? ''}`.trim() || s.email || 'Staff';
      return this._data.shifts.find(sh => sh.staffId === staffId)?.staffName || 'Former staff';
    };
    const colorOf = (staffId) => {
      const idx = Math.max(0, staff.findIndex(s => s.id === staffId));
      return AVATAR_COLORS[idx % AVATAR_COLORS.length];
    };
    const initialsOf = (name) => name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
    const fmtH = (min) => {
      const h = Math.floor(min / 60), m = Math.round(min % 60);
      return m === 60 ? `${h + 1}h` : m ? `${h}h ${m}m` : `${h}h`;
    };
    const fmtDay = (key) => new Date(key + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

    const statTile = (accent, label, val, sub) => `
      <div class="ts-stat-tile" style="--tile-accent:${accent}">
        <div class="ts-stat-tile-label">${label}</div>
        <div class="ts-stat-tile-val">${val}</div>
        <div class="ts-stat-tile-sub">${sub}</div>
      </div>`;
    const perHour = pool.pooledMinutes > 0 ? pool.pooledTips / (pool.pooledMinutes / 60) : 0;
    const statsHtml = `
      <div class="ts-stat-strip">
        ${statTile('#EC4899', 'Tips Collected', formatMoney(pool.totalTips), periodMeta.label ? esc(periodMeta.label) : 'this period')}
        ${statTile('#18AE79', 'Shared Out', formatMoney(pool.pooledTips), `to ${pool.people.length} ${pool.people.length === 1 ? 'person' : 'people'}`)}
        ${statTile('#3b82f6', 'Pooled Hours', fmtH(pool.pooledMinutes), 'on days with tips')}
        ${statTile('#f59e0b', 'Per Hour', pool.pooledMinutes > 0 ? formatMoney(perHour) : '–', 'tips per hour worked')}
      </div>`;

    const unassignedDays = pool.days.filter(d => !d.shares.length);
    const warnHtml = pool.unassigned > 0 ? `
      <div class="tp-warn" role="status">
        <span class="tp-warn-icon" aria-hidden="true">!</span>
        <p><b>${formatMoney(pool.unassigned)} isn't shared yet.</b> Nobody in the pool clocked a finished shift on
        ${unassignedDays.map(d => esc(fmtDay(d.day))).join(', ')}. Check the time clock for those days, or who shares tips below.</p>
      </div>` : '';

    const rolesHtml = `
      <section class="tp-card">
        <div class="tp-card-head">
          <div>
            <h4 class="tp-card-title">Who shares tips</h4>
            <p class="tp-card-hint">Tap a role to add or remove it. The owner never shares (US law bars owners and managers from an employee tip pool).</p>
          </div>
        </div>
        <div class="tp-role-chips">
          ${roles.length ? roles.map(r => {
            const on = poolIds.has(r.id);
            return `<button type="button" class="tp-role-chip${on ? ' tp-role-chip--on' : ''}" data-role-id="${esc(r.id)}" aria-pressed="${on}">
              <span class="tp-role-check" aria-hidden="true">${on ? '✓' : '+'}</span>${esc(r.name)}
            </button>`;
          }).join('') : '<p class="tp-card-hint">No roles yet. Add them in Staff.</p>'}
        </div>
      </section>`;

    const maxAmount = pool.people[0]?.amount || 1;
    const peopleHtml = pool.people.length ? `
      <section class="tp-card">
        <div class="tp-card-head"><h4 class="tp-card-title">Each person's share</h4></div>
        <div class="tp-people">
          ${pool.people.map(p => {
            const name = nameOf(p.staffId);
            return `
              <div class="tp-person">
                <span class="tp-avatar" style="background:${colorOf(p.staffId)}">${esc(initialsOf(name))}</span>
                <div class="tp-person-main">
                  <div class="tp-person-top">
                    <span class="tp-person-name">${esc(name)}</span>
                    <span class="tp-person-amount">${formatMoney(p.amount)}</span>
                  </div>
                  <div class="tp-bar"><span style="width:${Math.max(4, Math.round((p.amount / maxAmount) * 100))}%"></span></div>
                  <span class="tp-person-meta">${fmtH(p.minutes)} on ${p.days} day${p.days === 1 ? '' : 's'} with tips</span>
                </div>
              </div>`;
          }).join('')}
        </div>
      </section>` : '';

    const daysHtml = pool.days.length ? `
      <section class="tp-card">
        <div class="tp-card-head"><h4 class="tp-card-title">By day</h4></div>
        <div class="tp-days">
          ${pool.days.map(d => `
            <div class="tp-day${d.shares.length ? '' : ' tp-day--unassigned'}">
              <div class="tp-day-head">
                <span class="tp-day-date">${esc(fmtDay(d.day))}</span>
                <span class="tp-day-meta">${d.shares.length ? `${fmtH(d.minutes)} worked` : 'Nobody in the pool worked'}</span>
                <span class="tp-day-amount">${formatMoney(d.tips)}</span>
              </div>
              ${d.shares.length ? `<div class="tp-day-shares">${d.shares.map(s => `
                <span class="tp-share"><b>${esc(nameOf(s.staffId))}</b> ${fmtH(s.minutes)} · ${formatMoney(s.amount)}</span>`).join('')}</div>` : ''}
            </div>`).join('')}
        </div>
      </section>` : '';

    const emptyHtml = !pool.days.length ? `
      <div class="tp-empty">
        <div class="tp-empty-icon" aria-hidden="true">💝</div>
        <p class="tp-empty-title">No tips in this period</p>
        <p class="tp-empty-sub">${tipsEnabled
          ? 'Tips added at checkout show up here, split by who worked each day.'
          : 'Turn on tips in Settings > POS > Tips, and they\'ll be split here by who worked each day.'}</p>
      </div>` : '';

    this._content.innerHTML = `
      <div class="payroll-header">
        <h3 class="payroll-title">Tips</h3>
        <div class="payroll-header-actions">
          ${pool.days.length ? `<button class="payroll-export-btn" id="tipPoolExportCsv" type="button">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Export CSV
          </button>` : ''}
        </div>
      </div>
      ${this._periodBarHtml(periodMeta)}
      <p class="tp-intro">Each day's tips are split between the people who worked that day, by hours worked. Only finished shifts count.</p>
      ${pool.days.length ? statsHtml : ''}
      ${warnHtml}
      <div class="tp-grid">
        <div class="tp-col">${peopleHtml}${emptyHtml}${rolesHtml}</div>
        <div class="tp-col">${daysHtml}</div>
      </div>
    `;
    this._wirePeriodBar(periodMeta);

    this._content.querySelectorAll('.tp-role-chip').forEach(btn =>
      btn.addEventListener('click', () => {
        const ids = new Set(this._tipPoolRoleIds());
        if (ids.has(btn.dataset.roleId)) ids.delete(btn.dataset.roleId);
        else ids.add(btn.dataset.roleId);
        this._tipPoolRolesHandler?.([...ids]);
      }));

    document.getElementById('tipPoolExportCsv')?.addEventListener('click', () => {
      const q = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const rows = [['Date', 'Staff', 'Hours', 'Tip share', 'Day tips']];
      for (const d of [...pool.days].reverse()) {
        if (!d.shares.length) rows.push([d.day, '(nobody in the pool worked)', '', '', d.tips.toFixed(2)]);
        for (const s of d.shares) rows.push([d.day, nameOf(s.staffId), (s.minutes / 60).toFixed(2), s.amount.toFixed(2), d.tips.toFixed(2)]);
      }
      rows.push([], ['Total', 'Staff', 'Hours', 'Tip share']);
      for (const p of pool.people) rows.push(['', nameOf(p.staffId), (p.minutes / 60).toFixed(2), p.amount.toFixed(2)]);
      if (pool.unassigned > 0) rows.push(['', 'Not shared (nobody in the pool worked)', '', pool.unassigned.toFixed(2)]);
      const blob = new Blob(['﻿' + rows.map(r => r.map(q).join(',')).join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tips-${String(periodMeta.label || 'period').replace(/[^\w-]+/g, '-')}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    });
  }

  _renderLeaveSection() {
    const { canEdit, leaveRequests, staff } = this._data;

    // Dates are DATE columns (no time); parse as local midnight for display.
    const fmtDay = d => new Date(d + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    const dayCount = (r) => {
      const n = Math.round((new Date(r.endDate + 'T00:00:00') - new Date(r.startDate + 'T00:00:00')) / 86400000) + 1;
      return `${n} day${n === 1 ? '' : 's'}`;
    };
    const range = r => r.startDate === r.endDate ? fmtDay(r.startDate) : `${fmtDay(r.startDate)} – ${fmtDay(r.endDate)}`;
    // Soft emoji pills for type + status (per Kelly's mockup).
    const typeChip = t => t === 'sick'
      ? `<span class="ts-lv-pill ts-lv-pill--sick">🤒 Sick</span>`
      : `<span class="ts-lv-pill ts-lv-pill--vacation">✈️ Vacation</span>`;
    const avatar = (r) => {
      const name     = r.staffName || 'Staff';
      const initials = name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
      const idx      = Math.max(0, (staff ?? []).findIndex(s => s.id === r.staffId));
      return `<span class="ts-lv-avatar" style="background:${AVATAR_COLORS[idx % AVATAR_COLORS.length]}">${esc(initials)}</span>`;
    };

    const todayStr = (() => {
      const d = new Date();
      return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    })();

    const pending  = leaveRequests.filter(r => r.status === 'pending');
    // Nearest first: ongoing leaves (ending soonest) on top, then future ones
    // by how soon they start.
    const upcoming = leaveRequests
      .filter(r => r.status === 'approved' && r.endDate >= todayStr)
      .sort((a, b) => {
        const aOn = a.startDate <= todayStr, bOn = b.startDate <= todayStr;
        if (aOn !== bOn) return aOn ? -1 : 1;
        return aOn ? a.endDate.localeCompare(b.endDate) : a.startDate.localeCompare(b.startDate);
      });
    const history  = leaveRequests.filter(r => !pending.includes(r) && !upcoming.includes(r));
    const onLeaveToday = new Set(upcoming.filter(r => r.startDate <= todayStr).map(r => r.staffId)).size;

    // Ongoing / countdown status pill for an approved upcoming leave.
    const startsIn = (r) => {
      if (r.startDate <= todayStr) return `<span class="ts-lv-pill ts-lv-pill--now"><span class="ts-lv-pill-dot"></span>On leave now</span>`;
      const n = Math.round((new Date(r.startDate + 'T00:00:00') - new Date(todayStr + 'T00:00:00')) / 86400000);
      return `<span class="ts-lv-pill ts-lv-pill--soon">🕐 Starts ${n === 1 ? 'tomorrow' : `in ${n} days`}</span>`;
    };

    const pendingCard = (r) => `
      <li class="ts-lv-card" data-id="${esc(r.id)}">
        <div class="ts-lv-card-main">
          <div class="ts-lv-card-head">
            ${avatar(r)}
            <div class="ts-lv-card-who">
              <span class="ts-lv-card-name">${esc(r.staffName || 'Staff')}</span>
              <span class="ts-lv-card-range">📅 ${range(r)} · ${dayCount(r)}</span>
            </div>
            ${typeChip(r.type)}
          </div>
          ${r.reason ? `<p class="ts-lv-card-reason">“${esc(r.reason)}”</p>` : ''}
        </div>
        <div class="ts-lv-card-actions">
          <button class="btn ts-leave-reject" data-id="${esc(r.id)}" type="button">Reject</button>
          <button class="btn primary ts-leave-approve" data-id="${esc(r.id)}" type="button">Approve</button>
        </div>
      </li>`;

    const whoZone = (r) => `
      <div class="ts-lv-zone ts-lv-zone--who">
        ${avatar(r)}
        <div class="ts-lv-row-who">
          <span class="ts-lv-card-name">${esc(r.staffName || 'Staff')}</span>
          <span class="ts-lv-card-range">📅 ${range(r)} · ${dayCount(r)}</span>
        </div>
      </div>`;

    // Reviewer-outcome icons: quiet grey line icons (shield-check style, per the
    // mockup) - the emoji treatment stays on the type/status pills only.
    const shieldPath = `<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1 1 0 0 1 1.52 0C14.5 3.8 17 5 19 5a1 1 0 0 1 1 1z"/>`;
    const apprIcon = (kind) => {
      const inner = kind === 'ok' ? `${shieldPath}<path d="m9 12 2 2 4-4"/>`
        : kind === 'no'           ? `${shieldPath}<path d="m14.5 9.5-5 5"/><path d="m9.5 9.5 5 5"/>`
        :                           `<circle cx="12" cy="12" r="10"/><path d="m4.9 4.9 14.2 14.2"/>`;
      return `<svg class="ts-lv-appr-icon" xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">${inner}</svg>`;
    };

    const upcomingRow = (r) => `
      <li class="ts-lv-row" data-id="${esc(r.id)}">
        ${whoZone(r)}
        <div class="ts-lv-zone ts-lv-zone--status">
          ${typeChip(r.type)}
          ${startsIn(r)}
        </div>
        <div class="ts-lv-zone ts-lv-zone--approver">
          ${apprIcon('ok')}
          <div class="ts-lv-appr">
            <span class="ts-lv-appr-lbl">Approved by</span>
            <span class="ts-lv-appr-name">${esc(r.reviewedByName || '—')}</span>
          </div>
        </div>
      </li>`;

    const historyRow = (r) => {
      let outcome;
      if (r.status === 'approved') {
        outcome = `
          ${apprIcon('ok')}
          <div class="ts-lv-appr">
            <span class="ts-lv-appr-lbl">Approved by</span>
            <span class="ts-lv-appr-name">${esc(r.reviewedByName || '—')}</span>
          </div>`;
      } else if (r.status === 'rejected') {
        outcome = `
          ${apprIcon('no')}
          <div class="ts-lv-appr">
            <span class="ts-lv-appr-lbl">Rejected by</span>
            <span class="ts-lv-appr-name">${esc(r.reviewedByName || '—')}</span>
          </div>`;
      } else {
        outcome = `
          ${apprIcon('cancel')}
          <div class="ts-lv-appr">
            <span class="ts-lv-appr-lbl">Cancelled</span>
            <span class="ts-lv-appr-name">by staff</span>
          </div>`;
      }
      // Rejected rows show the whole conversation: what the staff member asked
      // for, and why it was declined.
      const msgs = (r.reason || (r.status === 'rejected' && r.reviewNote)) ? `
        <div class="ts-lv-msgs">
          ${r.reason ? `<span class="ts-lv-msg"><span class="ts-lv-msg-lbl">Request</span>“${esc(r.reason)}”</span>` : ''}
          ${r.status === 'rejected' && r.reviewNote ? `<span class="ts-lv-msg"><span class="ts-lv-msg-lbl ts-lv-msg-lbl--reject">Rejected</span>“${esc(r.reviewNote)}”</span>` : ''}
        </div>` : '';
      return `
        <li class="ts-lv-row ts-lv-row--history">
          ${whoZone(r)}
          <div class="ts-lv-zone ts-lv-zone--status ts-lv-zone--msgs">
            ${typeChip(r.type)}
            ${msgs}
          </div>
          <div class="ts-lv-zone ts-lv-zone--approver">${outcome}</div>
        </li>`;
    };

    const emptyBlock = (msg) => `
      <div class="ts-lv-empty">
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
        <span>${msg}</span>
      </div>`;

    this._content.innerHTML = `
      <div class="payroll-header">
        <h3 class="payroll-title">Leave</h3>
      </div>
      <p class="ts-section-sub">Vacation and sick leave requests. Staff file them from My Timesheet on the Time Clock${canEdit ? '; approving here marks the request - it does not change shifts or pay' : ''}.</p>

      <div class="ts-lv-tiles">
        ${canEdit ? `
        <div class="ts-lv-tile${pending.length ? ' ts-lv-tile--attn' : ''}">
          <span class="ts-lv-tile-num">${pending.length}</span>
          <span class="ts-lv-tile-lbl">Pending review</span>
        </div>` : ''}
        <div class="ts-lv-tile">
          <span class="ts-lv-tile-num">${upcoming.length}</span>
          <span class="ts-lv-tile-lbl">Upcoming leave${upcoming.length === 1 ? '' : 's'}</span>
        </div>
        <div class="ts-lv-tile">
          <span class="ts-lv-tile-num">${onLeaveToday}</span>
          <span class="ts-lv-tile-lbl">On leave today</span>
        </div>
      </div>

      ${canEdit ? `
      <h4 class="ts-leave-heading">Pending requests</h4>
      ${pending.length
        ? `<ul class="ts-lv-cards">${pending.map(pendingCard).join('')}</ul>`
        : emptyBlock('All caught up - no leave requests waiting for review.')}` : ''}

      <h4 class="ts-leave-heading">Upcoming leave</h4>
      ${upcoming.length
        ? `<ul class="ts-lv-list">${upcoming.map(upcomingRow).join('')}</ul>`
        : emptyBlock('No approved leave coming up.')}

      ${history.length ? `
      <details class="ts-leave-history">
        <summary class="ts-leave-history-summary">
          History
          <span class="ts-lv-count">${history.length}</span>
          <svg class="payroll-info-chevron" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
        </summary>
        <ul class="ts-lv-list">${history.map(historyRow).join('')}</ul>
      </details>` : ''}
    `;

    this._content.querySelectorAll('.ts-leave-approve').forEach(btn =>
      btn.addEventListener('click', () => this._reviewLeaveHandler?.(btn.dataset.id, 'approved')));
    this._content.querySelectorAll('.ts-leave-reject').forEach(btn =>
      btn.addEventListener('click', () => this._reviewLeaveHandler?.(btn.dataset.id, 'rejected')));
  }

  _reviewLeaveHandler = null;
  _addHandlerReviewLeave(handler) {
    this._reviewLeaveHandler = handler;
  }

  // ── Section: Deleted shifts ─────────────────────────────────────────────────

  _renderDeletedSection() {
    const { deletedShifts } = this._data;

    const delRows = deletedShifts.map(d => `
      <li class="payroll-deleted-row">
        <div class="payroll-deleted-main">
          <span class="payroll-deleted-name">${esc(d.staffName || 'Staff')}</span>
          <span class="payroll-deleted-times">${fmtDate(d.clockedInAt)} · ${fmtTime(d.clockedInAt)} → ${d.clockedOutAt ? fmtTime(d.clockedOutAt) : '–'}</span>
        </div>
        <span class="payroll-deleted-by">Deleted by ${esc(d.deletedBy)} · ${fmtDate(d.deletedAt)}</span>
        <button class="btn payroll-restore-btn" data-id="${esc(d.id)}" type="button">Restore</button>
      </li>`).join('');

    this._content.innerHTML = `
      <div class="payroll-header">
        <h3 class="payroll-title">Deleted shifts</h3>
      </div>
      <p class="ts-section-sub">Deleted shifts can be restored for 7 days, then they are removed permanently.</p>
      ${deletedShifts.length
        ? `<div class="payroll-deleted payroll-deleted--page"><ul class="payroll-deleted-list">${delRows}</ul></div>`
        : `<div class="ts-empty">
            ${trashSvg}
            <p>Nothing here - no shifts deleted in the last 7 days.</p>
          </div>`}
    `;

    this._content.querySelectorAll('.payroll-restore-btn').forEach(btn =>
      btn.addEventListener('click', () => this._restoreShiftHandler?.(btn.dataset.id)));
  }

  // ── Shift add/edit modal ────────────────────────────────────────────────────

  _showShiftModal(shift, staff) {
    if (document.getElementById('shiftEditModal')) return;
    const isEdit = !!shift;
    const modal = document.createElement('div');
    modal.className = 'cashier-picker-overlay';
    modal.id = 'shiftEditModal';
    modal.style.zIndex = '99999';

    const toLocalInput = iso => {
      if (!iso) return '';
      const d = new Date(iso);
      const pad = n => String(n).padStart(2,'0');
      return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };

    const staffOptions = staff.map(s => `<option value="${esc(s.id)}" ${shift?.staffId === s.id ? 'selected' : ''}>${esc(s.firstName)} ${esc(s.lastName)}</option>`).join('');

    modal.innerHTML = `
      <div class="cashier-picker-card" style="max-width:360px;width:calc(100vw - 40px);overflow:hidden">
        <div class="cashier-picker-header">
          <div><h3 class="cashier-picker-title">${isEdit ? 'Edit Shift' : 'Add Shift'}</h3></div>
          <button class="modal-close-btn" data-action="close" type="button">&times;</button>
        </div>
        <div class="edit-field" style="margin-top:8px">
          <label>Staff Member</label>
          <select id="shiftStaffSelect" class="settings-select">${staffOptions}</select>
        </div>
        <div class="edit-field" style="margin-top:10px">
          <label>Clock In</label>
          <input type="datetime-local" id="shiftClockIn" class="settings-input" value="${toLocalInput(shift?.clockedInAt)}" style="width:100%" />
        </div>
        <div class="edit-field" style="margin-top:10px">
          <label>Clock Out</label>
          <input type="datetime-local" id="shiftClockOut" class="settings-input" value="${toLocalInput(shift?.clockedOutAt)}" style="width:100%" />
        </div>
        <div class="edit-field" style="margin-top:10px">
          <label>Note (optional)</label>
          <input type="text" id="shiftNote" class="settings-input" placeholder="e.g. Covered for Jane" value="${esc(shift?.note ?? '')}" />
        </div>
        <div class="adj-form-actions" style="margin-top:16px">
          <button type="button" class="btn" data-action="close">Cancel</button>
          <button type="button" class="btn primary" data-action="save">Save</button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);
    const close = () => modal.remove();
    modal.querySelectorAll('[data-action="close"]').forEach(b => b.addEventListener('click', close));
    modal.addEventListener('click', e => { if (e.target === modal) close(); });
    modal.querySelector('[data-action="save"]').addEventListener('click', () => {
      const staffId     = document.getElementById('shiftStaffSelect')?.value;
      const clockedInAt = document.getElementById('shiftClockIn')?.value;
      const clockedOutAt= document.getElementById('shiftClockOut')?.value;
      const note        = document.getElementById('shiftNote')?.value.trim();
      if (!clockedInAt) { alert('Clock-in time is required.'); return; }
      close();
      if (this._shiftSaveHandler) this._shiftSaveHandler({
        id:           shift?.id ?? null,
        staffId,
        clockedInAt:  new Date(clockedInAt).toISOString(),
        clockedOutAt: clockedOutAt ? new Date(clockedOutAt).toISOString() : null,
        note,
      });
    });
  }

  _shiftSaveHandler = null;

  _addHandlerSaveShift(handler) {
    this._shiftSaveHandler = handler;
  }
}

const _view = new TimesheetView();
_view._addHandlerDrawer();
export default _view;
